/** Super admin › Coupons: discount codes for plan subscriptions (percent or amount off; once, for N months or
 * forever; plans and billing periods) or Lux purchases (percent or amount off a pack, or bonus Lux), with a
 * validity window, redemption limits, per-company limit and stacking; deactivate any time; redemption history.
 * Subscription coupons become Stripe coupons on save. Audited (`coupon.create`, `coupon.update`). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Plus, Power } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Badge, Button, Card, Empty, Field, Modal, PageHead, Select, Spinner, Switch, useToast } from "../../components/ui";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";
import type { MessageKey } from "../../i18n/keys";
import { couponEffect, couponWriteBody, emptyCouponDraft, type CouponDraft } from "../../lib/coupons";

type Billing = "monthly" | "annual";
const BILLING: Record<Billing, MessageKey> = { monthly: "admin.coupons.billingMonthly", annual: "admin.coupons.billingAnnual" };
/** Plan names are product names: never translated. */
const PLAN_NAMES: Record<string, string> = { pro: "Pro", business: "Business" };

function CouponForm({ onDone }: { onDone: () => void }) {
  const toast = useToast();
  const t = useT();
  const [d, setD] = useState<CouponDraft>(emptyCouponDraft());
  const set = (patch: Partial<CouponDraft>) => setD((x) => ({ ...x, ...patch }));
  const create = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/coupons", { body: couponWriteBody(d) })),
    onSuccess: (c) => {
      toast(t("admin.coupons.created", { code: c.code, inStripe: c.stripe_coupon_id ? "yes" : "no" }));
      setD(emptyCouponDraft());
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <div className="stack">
      <div className="coupon-form">
        <Field label={t("admin.coupons.code")}>
          <input className="input mono" value={d.code} maxLength={40} onChange={(e) => set({ code: e.target.value.toUpperCase() })} placeholder={t("admin.coupons.codePlaceholder")} dir="auto" />
        </Field>
        <Field label={t("admin.coupons.name")}>
          <input className="input" value={d.name} maxLength={100} onChange={(e) => set({ name: e.target.value })} placeholder={t("admin.coupons.namePlaceholder")} dir="auto" />
        </Field>
        <Field label={t("admin.coupons.appliesTo")}>
          <Select<"subscription" | "lux">
            value={d.applies_to}
            onChange={(v) => set({ applies_to: v, effect: v === "subscription" && d.effect === "bonus" ? "percent" : d.effect })}
            options={[
              { value: "subscription", label: t("admin.coupons.planSubscriptions") },
              { value: "lux", label: t("admin.coupons.luxPurchases") },
            ]}
          />
        </Field>
        <Field label={t("admin.coupons.discount")}>
          <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
            <Select<CouponDraft["effect"]>
              value={d.effect}
              onChange={(v) => set({ effect: v })}
              options={[
                { value: "percent", label: t("admin.coupons.percentOff") },
                { value: "amount", label: t("admin.coupons.amountOff") },
                ...(d.applies_to === "lux" ? [{ value: "bonus" as const, label: t("admin.coupons.bonusLux") }] : []),
              ]}
            />
            <input className="input" style={{ inlineSize: 100 }} inputMode="decimal" value={d.amount} onChange={(e) => set({ amount: e.target.value })} placeholder={d.effect === "amount" ? "50.00" : "20"} aria-label={t("admin.coupons.discountValue")} />
          </div>
        </Field>
        {d.effect === "bonus" && (
          <Field label={t("admin.coupons.bonusLasts")} hint={t("admin.coupons.bonusLastsHint")}>
            <input className="input" style={{ inlineSize: 90 }} inputMode="numeric" value={d.bonus_months} onChange={(e) => set({ bonus_months: e.target.value })} placeholder="12" aria-label={t("admin.coupons.bonusLastsMonths")} />
          </Field>
        )}
        {d.applies_to === "subscription" && (
          <>
            <Field label={t("admin.coupons.duration")}>
              <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
                <Select<CouponDraft["duration"]>
                  value={d.duration}
                  onChange={(v) => set({ duration: v })}
                  options={[
                    { value: "once", label: t("admin.coupons.durationOnce") },
                    { value: "repeating", label: t("admin.coupons.durationRepeating") },
                    { value: "forever", label: t("admin.coupons.durationForever") },
                  ]}
                />
                {d.duration === "repeating" && (
                  <input className="input" style={{ inlineSize: 70 }} inputMode="numeric" value={d.months} onChange={(e) => set({ months: e.target.value })} placeholder="3" aria-label={t("admin.coupons.durationMonths")} />
                )}
              </div>
            </Field>
            <Field label={t("admin.coupons.plansAndBilling")} hint={t("admin.coupons.plansAndBillingHint")}>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                {(["pro", "business"] as const).map((p) => (
                  <label key={p} className="small row" style={{ gap: 4 }}>
                    <input type="checkbox" checked={d.plans.includes(p)} onChange={() => set({ plans: toggle(d.plans, p) })} /> {PLAN_NAMES[p]}
                  </label>
                ))}
                {(["monthly", "annual"] as const).map((b) => (
                  <label key={b} className="small row" style={{ gap: 4 }}>
                    <input type="checkbox" checked={d.billings.includes(b)} onChange={() => set({ billings: toggle(d.billings, b) })} /> {t(BILLING[b])}
                  </label>
                ))}
              </div>
            </Field>
          </>
        )}
        <Field label={t("admin.coupons.validFrom")}>
          <input className="input" type="datetime-local" value={d.valid_from} onChange={(e) => set({ valid_from: e.target.value })} />
        </Field>
        <Field label={t("admin.coupons.validUntil")}>
          <input className="input" type="datetime-local" value={d.valid_until} onChange={(e) => set({ valid_until: e.target.value })} />
        </Field>
        <Field label={t("admin.coupons.maxRedemptions")} hint={t("admin.coupons.maxRedemptionsHint")}>
          <input className="input" inputMode="numeric" value={d.max_redemptions} onChange={(e) => set({ max_redemptions: e.target.value })} />
        </Field>
        <Field label={t("admin.coupons.perCompany")}>
          <input className="input" inputMode="numeric" value={d.per_company_limit} onChange={(e) => set({ per_company_limit: e.target.value })} />
        </Field>
        <Field label={t("admin.coupons.stacks")}>
          <Switch checked={d.stackable} onChange={(v) => set({ stackable: v })} label={d.stackable ? t("admin.common.yes") : t("admin.common.no")} />
        </Field>
      </div>
      <div>
        <Button variant="primary" icon={<Plus />} disabled={!d.code || !d.name || !d.amount} loading={create.isPending} onClick={() => create.mutate()}>
          {t("admin.coupons.create")}
        </Button>
      </div>
    </div>
  );
}

function Redemptions({ coupon, onClose }: { coupon: S["Coupon"]; onClose: () => void }) {
  const t = useT();
  const { date } = useFmt();
  const list = useQuery({
    queryKey: ["coupon-redemptions", coupon.id],
    queryFn: () => unwrap(api.GET("/v1/admin/coupons/{coupon_id}/redemptions", { params: { path: { coupon_id: coupon.id } } })),
  });
  return (
    <Modal title={t("admin.coupons.redemptionsTitle", { code: coupon.code })} onClose={onClose} width={720}>
      {!list.data ? (
        <Spinner />
      ) : list.data.items.length === 0 ? (
        <Empty icon={<History />} title={t("admin.coupons.notUsed")} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t("admin.coupons.company")}</th>
              <th>{t("admin.coupons.how")}</th>
              <th>{t("admin.coupons.status")}</th>
              <th>{t("admin.coupons.when")}</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => (
              <tr key={r.id}>
                <td dir="auto">{r.organization_name}</td>
                <td className="small">{t("admin.coupons.source", { source: r.source })}</td>
                <td>
                  <Badge tone={r.status === "redeemed" ? "ok" : r.status === "canceled" || r.status === "over_limit" ? "danger" : "info"}>{t("admin.coupons.redemptionStatus", { status: r.status })}</Badge>
                </td>
                <td className="small">{date(r.redeemed_at ?? r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

export function AdminCoupons() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const fmt = useFmt();
  const list = useQuery({ queryKey: ["admin-coupons"], queryFn: () => unwrap(api.GET("/v1/admin/coupons")) });
  const [open, setOpen] = useState<S["Coupon"] | null>(null);
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: S["CouponWrite"] }) =>
      unwrap(api.PATCH("/v1/admin/coupons/{coupon_id}", { params: { path: { coupon_id: id } }, body })),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["admin-coupons"] });
      toast(t("admin.coupons.toggled", { code: c.code, active: c.active ? "yes" : "no" }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <>
      <PageHead
        title={t("admin.coupons.title")}
        description={t("admin.coupons.description")}
      />
      <div className="stack">
        <Card title={t("admin.coupons.newCoupon")}>
          <CouponForm onDone={() => qc.invalidateQueries({ queryKey: ["admin-coupons"] })} />
        </Card>
        <Card title={t("admin.coupons.title")} pad={false}>
          {!list.data ? (
            <Spinner />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("admin.coupons.code")}</th>
                    <th>{t("admin.coupons.discount")}</th>
                    <th>{t("admin.coupons.for")}</th>
                    <th>{t("admin.coupons.valid")}</th>
                    <th className="num">{t("admin.coupons.used")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <b className="mono" dir="auto">{c.code}</b> {!c.active && <Badge tone="danger" plain>{t("admin.coupons.off")}</Badge>}
                        {c.stackable && <Badge plain>{t("admin.coupons.stacksBadge")}</Badge>}
                        <div className="muted small" dir="auto">{c.name}</div>
                      </td>
                      <td className="small">{couponEffect(c, t, fmt)}</td>
                      <td className="small">
                        {c.applies_to === "lux"
                          ? t("admin.coupons.luxPurchases")
                          : t("admin.coupons.forPlans", {
                              plans: intl.formatList((c.plans.length ? c.plans : ["pro", "business"]).map((p) => PLAN_NAMES[p] ?? p), { type: "unit" }),
                              billings: intl.formatList((c.billings.length ? c.billings : ["monthly", "annual"]).map((b) => (b in BILLING ? t(BILLING[b as Billing]) : b)), { type: "unit" }),
                            })}
                      </td>
                      <td className="small">
                        {t("admin.coupons.validRange", {
                          from: c.valid_from ? fmt.date(c.valid_from) : t("admin.coupons.validNow"),
                          until: c.valid_until ? fmt.date(c.valid_until) : t("admin.coupons.validNoEnd"),
                        })}
                      </td>
                      <td className="num">
                        {c.max_redemptions
                          ? t("admin.coupons.usedOfMax", { used: fmt.num(c.redemptions), max: fmt.num(c.max_redemptions) })
                          : fmt.num(c.redemptions)}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6, flexWrap: "nowrap", justifyContent: "flex-end" }}>
                          <Button size="sm" variant="ghost" icon={<History />} onClick={() => setOpen(c)}>
                            {t("admin.coupons.history")}
                          </Button>
                          <Button size="sm" icon={<Power />} onClick={() => update.mutate({ id: c.id, body: { active: !c.active } })}>
                            {c.active ? t("admin.coupons.deactivate") : t("admin.coupons.activate")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      {open && <Redemptions coupon={open} onClose={() => setOpen(null)} />}
    </>
  );
}
